<template>
  <div class="mobile-nav">
    <button v-if="openItem" class="mobile-submenu-backdrop" type="button" aria-label="Cerrar submenú" @click="openName = null" />
    <div class="mobile-chat">
      <Chat />
    </div>
    <div v-if="openItem?.children?.length" class="mobile-submenu">
      <button
        v-for="child in openItem.children"
        :key="child.name"
        type="button"
        class="mobile-submenu__item"
        :class="{ 'mobile-submenu__item--active': route.name === child.name }"
        @click="navigateTo(child.name)"
      >
        {{ t(child.displayName) }}
      </button>
    </div>
    <div class="mobile-bottom-bar bg-primary">
      <div
        v-for="item in visibleRoutes"
        :key="item.name"
        class="tab-icon"
        :class="{ active: isActive(item) }"
        @click="onTab(item)"
      >
        <VaIcon
          :name="item.meta?.icon || 'dashboard'"
          :size="isActive(item) ? '28px' : '20px'"
          class="transition-all duration-200"
          :class="{
            'text-white': isActive(item),
            'text-info material-symbols-outlined': !isActive(item),
          }"
        />
        <span class="tab-label" :class="isActive(item) ? 'text-white' : 'text-info'">
          {{ t(item.displayName || 'Sin nombre') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { INavigationRoute } from './NavigationRoutesDesktop'
import { visibleNavigationRoutes } from './visibleNavigation'
import { useUserStore } from '../../stores/user'
import Chat from '../../pages/chat/Chat.vue'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const openName = ref<string | null>(null)

const mobileHiddenRouteNames = ['reports', 'preferences', 'settings']
const personalMobileOrder = ['business-finances', 'facturacion', 'dashboard', 'accounts', 'budgets']

const visibleRoutes = computed(() => {
  const items = visibleNavigationRoutes({
    personal: userStore.isPersonalWorkspace,
    business: userStore.isBusinessWorkspace,
    billingEnabled: userStore.billingEnabled,
  }).filter((item) => !mobileHiddenRouteNames.includes(item.name))

  if (userStore.isPersonalWorkspace) {
    return personalMobileOrder
      .map((name) => items.find((item) => item.name === name))
      .filter((item): item is INavigationRoute => Boolean(item))
      .map((item): INavigationRoute => {
        if (item.name !== 'budgets') return item
        return {
          ...item,
          children: [
            { name: 'budgets', displayName: 'Presupuesto del mes', meta: { icon: 'savings', licence: true } },
            { name: 'funds', displayName: 'Fondos', meta: { icon: 'account_balance_wallet', licence: true } },
          ],
        }
      })
  }

  const home = items.find((item) => item.name === 'dashboard')
  const rest = items.filter((item) => item.name !== 'dashboard')
  if (!home) return items
  const leftCount = Math.floor(rest.length / 2)
  return [...rest.slice(0, leftCount), home, ...rest.slice(leftCount)]
})

const openItem = computed(() => visibleRoutes.value.find((item) => item.name === openName.value) ?? null)

function isActive(item: INavigationRoute) {
  if (route.name === item.name) return true
  return item.children?.some((child) => child.name === route.name) ?? false
}

function onTab(item: INavigationRoute) {
  if (item.children?.length) {
    openName.value = openName.value === item.name ? null : item.name
    return
  }
  navigateTo(item.name)
}

function navigateTo(name: string) {
  openName.value = null
  if (name && route.name !== name) router.push({ name })
}
</script>

<style scoped>
.mobile-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 30;
}

.mobile-submenu-backdrop {
  position: fixed;
  inset: 0;
  border: 0;
  background: transparent;
  z-index: -1;
}

.mobile-chat {
  position: relative;
  z-index: 1;
  margin: 0 0.5rem 0.85rem;
  padding: 0.45rem 0.5rem;
  background: #fff;
  border: 1px solid #d5dbe3;
  border-radius: 0.85rem;
}

.mobile-submenu {
  position: absolute;
  left: 0.5rem;
  right: 0.5rem;
  bottom: calc(64px + env(safe-area-inset-bottom) + 0.35rem);
  z-index: 2;
  margin: 0;
  padding: 0.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  background: var(--va-primary);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
}

.mobile-submenu__item {
  border: 0;
  background: transparent;
  color: #fff;
  text-align: left;
  padding: 0.7rem 0.85rem;
  border-radius: 0.5rem;
  font-size: 0.95rem;
}

.mobile-submenu__item--active {
  background: rgba(120, 255, 30, 0.12);
  color: var(--va-warning);
}

.mobile-bottom-bar {
  position: relative;
  z-index: 3;
  height: calc(64px + env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
  color: aliceblue;
  display: flex;
  align-items: center;
  justify-content: space-around;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.tab-icon {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 8px 4px;
  color: white;
  cursor: pointer;
  user-select: none;
}

.tab-icon.active {
  background-color: rgba(120, 255, 30, 0.1);
  border-radius: 12px;
}

.tab-label {
  margin-top: 0.2rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10px;
}
</style>
